import { useEffect, useState } from 'react';
import './QuickRollButton.css';
import type { RollTrigger } from '../../services/rollTriggers';

/**
 * V7.2.A Auto-roll-from-prose (2026-06-02) — inline Quick-roll button.
 *
 * Surfaces on a paragraph card when detectRollTrigger() returns a hit.
 * Clicking executes the same roll path the manual Roll-drawer uses, just
 * pre-filled with the detected skill + ease factor. The button disables
 * after a click and shows "Rolled" so the player doesn't accidentally
 * re-roll on the same paragraph.
 *
 * Skill availability: if the player's pregen doesn't have the detected
 * skill (e.g. paragraph asks for Cryptography but the PC has only
 * combat skills), the button still renders but is disabled with a
 * tooltip explaining why. The Roll drawer remains available for any
 * skill that the PC actually has.
 */

export interface QuickRollButtonProps {
  trigger: RollTrigger;
  /** True when the PC has this skill in their sheet. Drives disabled state. */
  available: boolean;
  /** Caller is responsible for running the actual roll resolution. */
  onRoll: (skill: string, ef: string) => void;
  /**
   * 2026-06-07 — when true, the button auto-fires once on mount (after a
   * 600ms beat so the player can see the trigger appear before the dice
   * cinematic takes over). Sourced from playerSettings.autoRollEnabled
   * by the parent.
   */
  autoFire?: boolean;
  testidPrefix?: string;
}

export function QuickRollButton({
  trigger,
  available,
  onRoll,
  autoFire = false,
  testidPrefix = 'br-quick-roll',
}: QuickRollButtonProps) {
  const [rolled, setRolled] = useState<boolean>(false);
  const disabled = !available || rolled;

  function handleClick() {
    if (disabled) return;
    setRolled(true);
    onRoll(trigger.skill, trigger.ef);
  }

  useEffect(() => {
    if (!autoFire) return;
    if (rolled) return;
    if (!available) return;
    const timer = window.setTimeout(() => {
      setRolled(true);
      onRoll(trigger.skill, trigger.ef);
    }, 600);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFire, available, trigger.skill, trigger.ef]);

  const title = !available
    ? `Your character does not have the ${trigger.skill} skill — open the Roll drawer to pick an alternative.`
    : rolled
      ? 'Already rolled for this paragraph.'
      : `Roll ${trigger.skill} at Ease Factor ${trigger.ef}.`;

  return (
    <button
      type="button"
      className="br-quick-roll"
      data-testid={testidPrefix}
      data-skill={trigger.skill}
      data-ef={trigger.ef}
      data-rolled={rolled ? 'true' : 'false'}
      disabled={disabled}
      title={title}
      onClick={handleClick}
    >
      <span className="br-quick-roll-icon" aria-hidden="true">
        🎲
      </span>
      <span
        className="br-quick-roll-skill"
        data-testid={`${testidPrefix}-skill`}
      >
        {rolled ? 'Rolled' : `Roll ${trigger.skill}`}
      </span>
      {!rolled && (
        <span
          className="br-quick-roll-ef"
          data-testid={`${testidPrefix}-ef`}
        >
          EF {trigger.ef}
        </span>
      )}
    </button>
  );
}

export default QuickRollButton;
